/**
 * CasualCompetitors 页面工具方法
 */
import moment, { Moment } from 'moment';
import { financeFormat, ratioValueFormat } from '@home/utils/common';
import { isEmpty } from '@intelligence/v3/utils/common';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { multiRatioMetricSort, generateSortMetricKeys } from '@intelligence/v3/utils/metric';
import { LANGUAGE_CN } from '@home/_config/config';
import { SortMode, SortType } from '@home/enums/common';
import { percentageFormat } from '@op/utils/value';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import { REVENUE_SCOPE_VALUES } from '@intelligence/utils/revenueScope';
import {
  IAA_GENRES,
  DEFAULT_PRIMARY_TAB,
  DEFAULT_SECONDARY_TAB,
  DEFAULT_SENSORTOWER_DATA_SOURCE,
  getDataSourceMetricKey,
  getDefaultSecondaryTabByPrimary,
  getSecondaryTabOptions,
  hasSecondaryGenreTabs,
} from '../constants';

import type { ICasualCompetitorsDefaultGenre, ICasualCompetitorsParams } from '../types';

/**
 * 判断是否为中文语言
 * @param locale 当前语言
 * @returns 是否为中文
 */
export const isChineseLanguage = (locale: string): boolean => {
  return locale === LANGUAGE_CN;
};

export const MAX_RATIO_DISPLAY_VALUE = 9999;

export const getTranslatedText = (formatMessage: Function, text?: string): string | React.ReactElement => {
  if (!text) {
    return '';
  }

  if (typeof formatMessage !== 'function') {
    return text;
  }

  let result = '';

  try {
    result = formatMessage({ id: text });
  } catch (error) {
    result = text;
  }

  return result;
};

/**
 * 判断日期范围是否为单日或单月
 * @param filterValue 筛选器值
 * @returns 是否为单日或单月
 */
export const isSingleDateRange = (filterValue?: IRecord): boolean => {
  const rangeTime = filterValue?.[filterKeyMap.RANGE_TIME];
  const granularityType = rangeTime?.type || 'month';
  const granularity = granularityType === 'month' ? 'monthly' : 'daily';
  const dateValue = rangeTime?.value || [];
  const startDate = dateValue[0] ? moment(dateValue[0]) : null;
  const endDate = dateValue[1] ? moment(dateValue[1]) : null;

  if (!startDate || !endDate) {
    return false;
  }

  if (granularity === 'monthly') {
    return startDate.isSame(endDate, 'month');
  }

  return startDate.isSame(endDate, 'day');
};

const dateTypeMap: Record<string, string> = {
  date: 'date',
  day: 'date',
  daily: 'date',
  week: 'week',
  weekly: 'week',
  month: 'month',
  monthly: 'month',
};
export const getValidDateType = (dateType?: string): string => {
  if (isEmpty(dateType)) {
    return 'month';
  }

  // @ts-ignore
  return dateTypeMap[dateType] || 'month';
};

/**
 * 获取最近一个月的日期范围
 * @returns [startDate, endDate] 日期范围
 */
export const getLastMonthRange = (): [Moment, Moment] => {
  const startDate = moment().subtract(1, 'months').startOf('month');
  const endDate = moment().subtract(1, 'months').endOf('month');
  return [startDate, endDate];
};

/**
 * 百分比格式化（自动乘以100）
 * @param data 小数形式的百分比，如 0.25 表示 25%
 * @param num 保留的小数位数，默认2
 * @param fillZero 位数不够是否填充0，默认false
 * @param emptyValue 无数据时返回的内容，默认为空 ''
 * @returns 格式化后的百分比字符串，如 '25%'
 */
export const percentFormat = (
  data: string | number,
  num = 2,
  fillZero = false,
  emptyValue: string | number = '',
): string | number => {
  if (isEmpty(data)) {
    return emptyValue;
  }

  // 非数字值处理
  const dataNum = Number(data);
  if (isNaN(dataNum)) {
    return data;
  }

  // 乘以100后调用 ratioValueFormat，isPercent 设为 true 表示已经是乘以100的数值
  return ratioValueFormat(dataNum * 100, num, fillZero, emptyValue, true);
};

/**
 * 格式化数值（KMB格式）
 * @param value 数值
 * @param emptyValue 无数据时返回的内容，默认为 '-'
 * @returns 格式化后的数值字符串，如 '1.23M'
 */
export const formatKMBValue = (value: number | undefined, emptyValue = '-'): string => {
  if (value === undefined || value === null) {
    return emptyValue;
  }

  // 使用公共 financeFormat 方法格式化
  return financeFormat(value, 2, false, emptyValue) as string;
};

/**
 * 缩放至百分比数值（乘以100）
 * @param value 小数值，如 0.25
 * @returns 百分比数值，如 25
 */
export const scaleToPercent = (value: number | string | undefined | null): number => {
  if (value === undefined || value === null) {
    return 0;
  }

  const numValue = Number.parseFloat(value as string);

  if (Number.isNaN(numValue)) {
    return 0;
  }

  return numValue * 100;
};

/**
 * 格式化页面内环比展示文本（保留颜色/箭头逻辑，文案统一做上下限截断）
 */
export const formatLimitedRatioDisplay = (value: number | undefined | null, decimalDigit = 2): string | number => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return '-';
  }

  return percentageFormat(value, '-', decimalDigit);
};

/**
 * 格式化当前筛选时间范围，用于柱状图/饼图 tooltip 标题。
 */
export const formatTooltipTimeRange = (
  granularity?: 'monthly' | 'daily' | string,
  startDate?: string,
  endDate?: string,
): string => {
  if (isEmpty(startDate) || isEmpty(endDate)) {
    return '';
  }

  const dateFormat = granularity === 'monthly' ? 'YYYY-MM' : 'YYYY-MM-DD';
  return `${formatDateStr(startDate, dateFormat)}~${formatDateStr(endDate, dateFormat)}`;
};

// ==================== 品类相关工具方法 ====================

/**
 * 过滤收入类指标
 * @description 当 showRevenue 为 false 时，过滤掉包含 iap_revenue 的指标
 * @param metrics 指标列表
 * @param showRevenue 是否显示收入指标
 * @returns 过滤后的指标列表
 */
export const filterRevenueMetrics = <T extends string>(metrics: T[], showRevenue: boolean): T[] => {
  if (showRevenue) {
    return metrics;
  }
  return metrics.filter((m) => !m.includes('iap_revenue')) as T[];
};

/**
 * 获取当前品类 genreId
 * @param primaryTab 主 Tab 值
 * @param secondaryTab 次级 Tab 值
 * @returns genreId
 */
export const getGenreId = (primaryTab: string, secondaryTab: string): string => {
  return hasSecondaryGenreTabs(primaryTab) ? secondaryTab : primaryTab;
};

/**
 * 判断是否显示收入指标
 * @param genreId 品类 ID
 * @returns 是否显示收入指标
 */
export const hasRevenueMetric = (genreId: string): boolean => {
  return !IAA_GENRES.includes(genreId);
};

/**
 * 判断是否品类类型选择的是 IAA / IAA All
 * @param primaryTab 主 品类类型
 * @param secondaryTab 副 品类类型
 * @return 是否为 IAA / IAA All
 */
export const getIsIAAAll = (primaryTab: string, secondaryTab: string): boolean => {
  return primaryTab === 'iaa' && secondaryTab === 'iaa_all';
};

/**
 * 判断是否品类类型选择的是 IAA / IAA Arrows
 * @param primaryTab 主 品类类型
 * @param secondaryTab 副 品类类型
 * @return 是否为 IAA / IAA Arrows
 */
export const getIsIAAArrows = (primaryTab: string, secondaryTab: string): boolean => {
  return primaryTab === 'iaa' && secondaryTab === 'iaa_arrows';
};

/**
 * 从 filterValue 提取接口参数
 * @param filterValue 筛选器值
 * @returns 接口参数
 */
export const extractApiParams = (filterValue: IRecord) => {
  const rangeTime = filterValue?.[filterKeyMap.RANGE_TIME];
  const granularityType = rangeTime?.type || 'month';
  const granularity = granularityType === 'month' ? 'monthly' : 'daily';
  const dateValue = rangeTime?.value || [];
  const startDate = dateValue[0] ? moment(dateValue[0]).format('YYYY-MM-DD') : '';
  const endDate = dateValue[1] ? moment(dateValue[1]).format('YYYY-MM-DD') : '';
  const markets = filterValue?.[filterKeyMap.REGION]?.markets || ['global'];
  const platforms = filterValue?.[filterKeyMap.PLATFORM]?.value || [];

  return { granularity, startDate, endDate, markets, platforms };
};

/**
 * 从 filterValue 提取折线图接口参数（扩展日期范围）
 * @description 折线图需要展示趋势，需要扩展日期范围：
 * - 日粒度：只有开始日期跟结束日期是同一天时，往前推6天，补齐7天
 * - 月粒度：只有开始日期跟结束日期是同一个月时，往前推5个月，补齐6个月
 * @param filterValue 筛选器值
 * @returns 接口参数
 */
export const extractChartApiParams = (filterValue: IRecord) => {
  const rangeTime = filterValue?.[filterKeyMap.RANGE_TIME];
  const granularityType = rangeTime?.type || 'month';
  const granularity = granularityType === 'month' ? 'monthly' : 'daily';
  const dateValue = rangeTime?.value || [];
  const originalStartDate = dateValue[0] ? moment(dateValue[0]) : null;
  const originalEndDate = dateValue[1] ? moment(dateValue[1]) : null;

  let startDate = originalStartDate ? originalStartDate.format('YYYY-MM-DD') : '';
  const endDate = originalEndDate ? originalEndDate.format('YYYY-MM-DD') : '';

  // 判断是否需要扩展日期范围
  let shouldExtend = false;
  if (originalStartDate && originalEndDate) {
    if (granularity === 'monthly') {
      // 月粒度：判断开始日期和结束日期是否在同一个月
      shouldExtend = originalStartDate.isSame(originalEndDate, 'month');
    } else {
      // 日粒度：判断开始日期和结束日期是否是同一天
      shouldExtend = originalStartDate.isSame(originalEndDate, 'day');
    }
  }

  // 根据粒度扩展开始日期（使用 clone 避免变更原始 moment 对象）
  if (shouldExtend && originalStartDate) {
    if (granularity === 'monthly') {
      // 月粒度：往前推5个月
      startDate = originalStartDate.clone().subtract(5, 'months').format('YYYY-MM-DD');
    } else {
      // 日粒度：往前推6天
      startDate = originalStartDate.clone().subtract(6, 'days').format('YYYY-MM-DD');
    }
  }

  const markets = filterValue?.[filterKeyMap.REGION]?.markets || ['global'];
  const platforms = filterValue?.[filterKeyMap.PLATFORM]?.value || [];

  return { granularity, startDate, endDate, markets, platforms };
};

/**
 * 重点游戏默认排序状态
 */
export const createDefaultKeyGamesSortState = (
  dataSource = DEFAULT_SENSORTOWER_DATA_SOURCE,
): IRatioMetricSortState<IRecord> => {
  return {
    dataSource,
    metric: 'daily_avg_dau',
    type: SortType.Desc,
    mode: SortMode.Number,
  };
};

export const DEFAULT_KEY_GAMES_SORT_STATE = createDefaultKeyGamesSortState();

/**
 * 归一化重点游戏排序状态
 * @description 当排序状态为空或未指定排序方向时，回退到默认排序
 */
export const normalizeMetricSortState = (
  sortState?: IRatioMetricSortState<IRecord>,
  dataSource = DEFAULT_SENSORTOWER_DATA_SOURCE,
): IRatioMetricSortState<IRecord> => {
  const defaultSortState = createDefaultKeyGamesSortState(dataSource);

  if (!sortState?.type) {
    return { ...defaultSortState };
  }

  return {
    ...defaultSortState,
    ...sortState,
    dataSource: sortState?.dataSource || defaultSortState.dataSource,
    metric: sortState?.metric || defaultSortState.metric,
    type: sortState?.type || defaultSortState.type,
    mode: sortState?.mode ?? defaultSortState.mode,
  };
};

/**
 * 根据排序状态获取接口 order_by 值
 */
export const getSortOrderBys = (
  sortState?: IRatioMetricSortState<IRecord>,
  dataSource = DEFAULT_SENSORTOWER_DATA_SOURCE,
): string[] => {
  const normalizedSortState = normalizeMetricSortState(sortState, dataSource);
  return generateSortMetricKeys([normalizedSortState]);
};

/**
 * 根据当前排序状态获取前N个游戏ID
 * @param data 游戏列表数据
 * @param sortState 排序状态
 * @param count 获取的数量，默认5
 * @returns 前N个游戏的ID列表
 */
export const getTopNGameIds = (
  data: IRecord[],
  sortState: IRatioMetricSortState<IRecord> | undefined,
  count = 5,
  dataSource = DEFAULT_SENSORTOWER_DATA_SOURCE,
): string[] => {
  if (isEmpty(data)) {
    return [];
  }

  const defaultMetricKey = getDataSourceMetricKey(dataSource, 'daily_avg_dau');

  // 使用 multiRatioMetricSort 进行排序
  const sortedData = sortState
    ? multiRatioMetricSort(data, [sortState])
    : [...data].sort((a, b) => {
      const aValue = a?.[defaultMetricKey] ?? 0;
      const bValue = b?.[defaultMetricKey] ?? 0;
      return bValue - aValue;
    });

  return sortedData.slice(0, count).map((item: any) => item?.game_id);
};

const getDefaultPrimaryTab = (defaultCompetitorsGenre?: ICasualCompetitorsDefaultGenre): string => {
  return defaultCompetitorsGenre?.primaryTab || DEFAULT_PRIMARY_TAB;
};

const getDefaultSecondaryTab = (defaultCompetitorsGenre?: ICasualCompetitorsDefaultGenre): string => {
  return defaultCompetitorsGenre?.secondaryTab || DEFAULT_SECONDARY_TAB;
};

export const resolveGenreTabs = (
  primaryTabValue: string | undefined,
  secondaryTabValue: string | undefined,
  defaultCompetitorsGenre?: ICasualCompetitorsDefaultGenre,
) => {
  const defaultPrimaryTab = getDefaultPrimaryTab(defaultCompetitorsGenre);
  const defaultSecondaryTab = getDefaultSecondaryTab(defaultCompetitorsGenre);
  const finalPrimaryTab = primaryTabValue || defaultPrimaryTab;

  let finalSecondaryTab = '';
  if (hasSecondaryGenreTabs(finalPrimaryTab)) {
    const secondaryOptions = getSecondaryTabOptions(finalPrimaryTab);
    const preferredSecondaryTab =
      finalPrimaryTab === defaultPrimaryTab ? defaultSecondaryTab : '';
    const isPreferredValid = secondaryOptions.some((opt) => opt.value === preferredSecondaryTab);
    const fallbackSecondaryTab = isPreferredValid
      ? preferredSecondaryTab
      : getDefaultSecondaryTabByPrimary(finalPrimaryTab);
    const isValidSecondary = secondaryOptions.some((opt) => opt.value === secondaryTabValue);
    finalSecondaryTab = isValidSecondary ? (secondaryTabValue as string) : fallbackSecondaryTab;
  }

  return {
    primaryTab: finalPrimaryTab,
    secondaryTab: finalSecondaryTab,
  };
};

// ==================== 参数构建工具方法 ====================

/**
 * 构建统一的参数对象
 * @param primaryTab 主品类 Tab
 * @param secondaryTab 次级品类 Tab
 * @param filterValue 筛选器值
 * @returns 统一参数对象
 */
export const buildParams = (
  primaryTab: string,
  secondaryTab: string,
  filterValue: IRecord,
  enableRevenueScope = false,
): ICasualCompetitorsParams => {
  const genreId = getGenreId(primaryTab, secondaryTab);
  const showRevenue = hasRevenueMetric(genreId);
  const isIAAAll = getIsIAAAll(primaryTab, secondaryTab);
  const isIAAArrows = getIsIAAArrows(primaryTab, secondaryTab);
  const isSingleDate = isSingleDateRange(filterValue);

  const apiParams = extractApiParams(filterValue);
  const chartParams = extractChartApiParams(filterValue);

  const params = {
    primaryTab,
    secondaryTab,
    genreId,
    granularity: apiParams.granularity as 'monthly' | 'daily',
    startDate: apiParams.startDate,
    endDate: apiParams.endDate,
    chartStartDate: chartParams.startDate,
    markets: apiParams.markets,
    platforms: apiParams.platforms,
    showRevenue,
    isIAAAll,
    isIAAArrows,
    isSingleDate,
  };

  if (enableRevenueScope) {
    const revenueScope = filterValue?.[filterKeyMap.REVENUE_SCOPE]?.value || REVENUE_SCOPE_VALUES.EXCLUDING;
    Object.assign(params, { revenueScope });
  }

  return params;
};
